import { EventEmitter } from 'events'
import { debounce } from 'throttle-debounce'

export type Layout = 'width' | 'height'

export interface Options {
  windowId: string
  url: string
  /**
   * 兼容布局 width 以外容器宽对齐，高自适应、height 以外容器高对齐，宽自适应，设置width需要设置外容器运行滚动
   * default 'height'
   */
  layout?: Layout
}

export interface Size {
  width: number
  height: number
}

export interface Line {
  /**
   * 0 默认、1 按下、2 移动、3 抬起
   */
  state: 0 | 1 | 2 | 3
  x: number
  y: number
  lineColor?: string
  lineWidth?: number
  isEraser?: boolean
}

export interface DrawLines {
  alph?: number
  /**
   * 不带#
   */
  brushColor: string
  /**
   * 是否橡皮
   */
  isEraser?: boolean
  line: Line[]
  /**
   * 线宽度
   */
  lineWidth: number
  /**
   * 线ID
   */
  windowId: string
  /**
   * 画线人
   */
  userId?: string
}

export interface AddLines {
  alph?: number
  /**
   * 不带#
   */
  brushColor?: string
  /**
   * 是否橡皮
   */
  isEraser?: boolean
  line: Line[]
  /**
   * 线宽度
   */
  lineWidth?: number
  /**
   * 线ID
   */
  windowId?: string
  /**
   * 画线人
   */
  userId?: string
}

export interface Conf {
  brushColor: string
  lineWidth: number
  // 画布状态 0、鼠标 1、画笔 2、橡皮
  tool: 0 | 1 | 2
  eraserWidth: number
  eraserHeight: number
}

export interface SetConf {
  brushColor?: string
  lineWidth?: number
  tool?: 0 | 1 | 2
  eraserWidth?: number
  eraserHeight?: number
}

export interface PageData {
  [key: string]: DrawLines[]
}

export class Board extends EventEmitter {
  public windowId: string = ''
  public canvas: HTMLCanvasElement | null = null
  public basicWidth: number = 996 // 基准宽度，缩放屏幕会以此数值运算画线宽度
  private ctx: CanvasRenderingContext2D | null = null
  private dom: HTMLDivElement | null = null
  private layout: Layout = 'height'
  /**
   * 是否开始画线
   */
  private isStart: boolean = false
  /**
   * 图片高宽比
   */
  private ratio: number = 1
  /**
   * 线汇总
   */
  private pagesData: PageData = {}
  /**
   * 被撤销的线
   */
  private tempList: PageData = {}
  /**
   * 画下的线条
   */
  private line: Line[] = []
  /**
   * 画线的时间
   */
  private drawTime: number = 0
  /**
   * 线颜色、线宽度
   * @see line.
   */
  public conf: Conf = {
    brushColor: 'E02020',
    lineWidth: 3,
    tool: 0,
    // 橡皮宽度
    eraserWidth: 20,
    // 橡皮高度
    eraserHeight: 40
  }

  constructor() {
    super()
  }

  /**
   * 初始化元素
   * @param options @see Options
   * @returns Promise<Board>
   */
  public create(ele: HTMLDivElement | string) {
    if (typeof ele === 'string') {
      this.dom = document.querySelector(ele) as HTMLDivElement
    } else {
      this.dom = ele
    }
  }

  /**
   * 使用一张图片初始化白板
   * @param options @see Options
   * @returns Promise<Board>
   */
  public async init(options: Options): Promise<Board> {
    if (!options.windowId || !options.url || !this.dom) {
      super.emit('error', 'Board: windowId 与 url 不能为空')
      return Promise.reject('Board: windowId 与 url 不能为空')
    }
    try {
      // console.log('Board: init')
      this.canvas = document.createElement('canvas')
      this.windowId = options.windowId
      this.canvas.id = options.windowId
      const size = await Board.getImg(options.url)
      this.ratio = size.width / size.height
      const canvasSize = {
        width: 0,
        height: 0
      }
      this.layout = options.layout || this.layout
      if (this.layout === 'width' || this.dom.clientHeight * this.ratio > this.dom.clientWidth) {
        canvasSize.width = this.dom.clientWidth
        canvasSize.height = this.dom.clientWidth / this.ratio
      } else {
        canvasSize.height = this.dom.clientHeight
        canvasSize.width = this.dom.clientHeight * this.ratio
      }
      this.canvas.width = canvasSize.width
      this.canvas.height = canvasSize.height
      this.ctx = this.canvas.getContext('2d')
      this.canvas.style.background = `url(${options.url}) top/100% no-repeat`
      this.canvas.style.margin = '0 auto'
      // this.canvas.style.borderStyle = 'dashed'
      // this.canvas.style.borderWidth = '0 1px 0 1px'
      // this.canvas.style.borderColor = 'rgba(255,255,255,0.2)'
      this.dom && (this.dom.innerHTML = '')
      this.dom && this.dom.appendChild(this.canvas)
      if (!this.pagesData[this.windowId]) {
        this.pagesData[this.windowId] = []
        this.tempList[this.windowId] = []
      }
      this.pageDraw()
      this.removeAllEventListener()
      window.addEventListener('mousedown', this._drawDown.bind(this))
      window.addEventListener('mousemove', this._drawMove.bind(this))
      window.addEventListener('mouseup', this._drawUp.bind(this))
      window.addEventListener('resize', debounce(300, this._resize.bind(this)))
      super.emit('ready', true)
    } catch (e) {
      console.log(e)
      super.emit('error', 'Board: 加载图片错误')
    }
    return this
  }

  /**
   * 图片高宽
   * @param url 图片
   * @returns Promise<Size>
   */
  public static async getImg(url: string): Promise<Size> {
    const img = new Image()
    img.src = url
    return new Promise((resolve) => {
      if (img.complete) {
        resolve({ width: img.width, height: img.height })
      }
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = () => {
        resolve({ width: 0, height: 0 })
      }
    })
  }

  /**
   * 鼠标按下
   */
  private _drawDown(evt: MouseEvent) {
    const target = evt.target
    if (!this.canvas || target !== this.canvas || this.isStart) return
    this.isStart = true
    this._draw(evt)
  }

  /**
   * 鼠标移动
   */
  private _drawMove(evt: MouseEvent) {
    if (!this.isStart) {
      return
    }
    this._draw(evt)
  }

  /**
   * 鼠标抬起
   */
  private _drawUp(evt: MouseEvent) {
    this._draw(evt)
    this.isStart = false
  }

  /**
   * 改变画布大小
   */
  public _resize() {
    if (!this.canvas || !this.dom) return
    const canvasSize = {
      width: 0,
      height: 0
    }
    if (this.layout === 'width' || this.dom.clientHeight * this.ratio > this.dom.clientWidth) {
      canvasSize.width = this.dom.clientWidth
      canvasSize.height = this.dom.clientWidth / this.ratio
    } else {
      canvasSize.height = this.dom.clientHeight
      canvasSize.width = this.dom.clientHeight * this.ratio
    }
    this.canvas.setAttribute('width', `${canvasSize.width}`)
    this.canvas.setAttribute('height', `${canvasSize.height}`)
    requestAnimationFrame(() => {
      this.pageDraw()
    })
  }

  /**
   * 画线
   */
  private _draw(evt: MouseEvent) {
    const target = evt.target
    if (this.conf.tool === 0 || !this.canvas || !this.ctx || !this.isStart) return
    const type = evt.type
    const canvasRect = this.canvas.getBoundingClientRect()
    const x = evt.clientX - canvasRect.left
    const y = evt.clientY - canvasRect.top
    if (type === 'mousedown') {
      this.ctx.beginPath()
      this.ctx.moveTo(x, y)
      this.ctx.lineCap = 'round'
      this.ctx.lineJoin = 'round'
      this.ctx.lineWidth = this.conf.lineWidth
      this.ctx.strokeStyle = this.conf.brushColor.includes('#') ? this.conf.brushColor : `#${this.conf.brushColor}`
      this.line.push({
        x: x / this.canvas.width,
        y: y / this.canvas.height,
        state: 1,
        lineColor: this.conf.brushColor.includes('#') ? this.conf.brushColor : `#${this.conf.brushColor}`,
        lineWidth: this.conf.lineWidth,
        isEraser: false
      })
      const now = +new Date()
      if (now - this.drawTime > 10) {
        this.drawTime = now
        this.line.push({
          x: x / this.canvas.width,
          y: y / this.canvas.height,
          state: 1,
          lineColor: this.conf.brushColor.includes('#') ? this.conf.brushColor : `#${this.conf.brushColor}`,
          lineWidth: this.conf.lineWidth,
          isEraser: false
        })
      }
    }
    if (type === 'mousemove') {
      if (this.conf.tool === 1) {
        this.ctx.lineTo(x, y)
        this.ctx.stroke()
      } else if (this.conf.tool === 2) {
        this.ctx.clearRect(x, y, this.conf.eraserWidth, this.conf.eraserHeight)
      }

      this.line.push({
        x: x / this.canvas.width,
        y: y / this.canvas.height,
        state: 2,
        isEraser: false
      })
    }
    if (type === 'mouseup') {
      this.ctx.closePath()
      this.line.push({
        x: x / this.canvas.width,
        y: y / this.canvas.height,
        state: 3,
        isEraser: false
      })
      const isWork = this.line.find((v) => v.state === 2)
      if (this.line.length > 2 && isWork) {
        this.saveLines()
      }
    }
  }

  /**
   * 画线保存并抛出
   */
  private saveLines() {
    const lines = {
      alph: 1,
      brushColor: this.conf.brushColor.includes('#') ? this.conf.brushColor.replace('#', '') : this.conf.brushColor,
      isEraser: false,
      line: this.line,
      lineWidth: this.conf.lineWidth,
      windowId: this.windowId
    }
    this.pagesData[this.windowId].push(lines)
    super.emit('emitArtDrawLine', lines)
    this.line = []
  }

  /**
   * 添加一条画线，不是当前页面不显示
   */
  public addPartionLine(lines: AddLines) {
    this.pagesData[this.windowId].push({
      alph: 1,
      brushColor: this.conf.brushColor.includes('#') ? this.conf.brushColor.replace('#', '') : this.conf.brushColor,
      isEraser: false,
      lineWidth: this.conf.lineWidth,
      windowId: this.windowId,
      ...lines
    })
    if (lines.windowId === this.windowId) {
      this.pageDraw()
    }
  }

  /**
   * 绘制整个页面的画线
   */
  public pageDraw(windowId?: string) {
    const winId = windowId || this.windowId
    if (!this.ctx || !this.canvas) return
    const { width, height } = this.canvas
    const ctx = this.ctx
    const pageLines = this.pagesData[winId] || []
    this.ctx.clearRect(0, 0, width, height)
    if (pageLines.length) {
      pageLines.forEach((pageLine) => {
        const { line, lineWidth, brushColor } = pageLine
        const dynamicLineWidth = Math.max(1, lineWidth / (this.basicWidth / width))
        line.forEach(({ x, y, state }) => {
          const ex = width * x
          const ey = height * y
          if (state === 1) {
            ctx.beginPath()
            ctx.moveTo(ex, ey)
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'
            // 缩放时调整线宽
            ctx.lineWidth = dynamicLineWidth
            ctx.strokeStyle = `#${brushColor}`
          }
          if (state === 2) {
            ctx.lineTo(ex, ey)
            ctx.stroke()
          }
          if (state === 3) {
            ctx.closePath()
          }
        })
      })
    }
  }

  /**
   * 设置初始画线
   */
  public setPagesData(data: PageData) {
    this.pagesData = data
  }

  /**
   * 获取页面画线
   */
  public getPageLines(windowId: string): DrawLines[] {
    return this.pagesData[windowId]
  }

  /**
   * 获取所有页面画线
   */
  public getPagesData(): { windowId: string; Lines: DrawLines[] }[] {
    const data: { windowId: string; Lines: DrawLines[] }[] = []
    Object.keys(this.pagesData).forEach((windowId) => {
      data.push({ windowId, Lines: this.pagesData[windowId] })
    })
    return data
  }

  /**
   * 设置画笔宽度与颜色
   */
  public setConf(conf: SetConf) {
    // console.log('setConf', conf)
    this.conf = { ...this.conf, ...conf }
    super.emit('conf', this.conf)
  }

  /**
   * 画线操作
   * @param action 1 上一步、2 下一步、3 清空页面和恢复库、6 清空页面的恢复库、7 清理页面
   * @param windowId 页面ID，为空则当前页面
   * @param isRevokeAll true 操作所有人的划线 false 操作指定userId画线数据，需要传userId（action=1有效）
   * @param userId 操作人id
   */
  public clear(action: 1 | 2 | 3 | 6 | 7, windowId?: string, isRevokeAll: boolean = true, userId?: string) {
    if (!this.windowId) return
    const winId = windowId || this.windowId
    const pageLines: DrawLines[] = this.pagesData[winId]
    const tempList: DrawLines[] = this.tempList[winId]
    switch (action) {
      case 1:
        if (pageLines.length) {
          if (!tempList) this.tempList[winId] = []
          if (!isRevokeAll && userId) {
            const index = pageLines.findLastIndex((v) => v.userId === userId)
            if (index >= 0) {
              this.tempList[winId].push(pageLines[index])
              this.pagesData[winId].splice(index, 1)
            }
          } else {
            this.tempList[winId].push(pageLines[pageLines.length - 1])
            this.pagesData[winId].length--
          }
          this.pageDraw()
        }
        break
      case 2:
        if (tempList && tempList.length) {
          this.pagesData[winId].push(tempList[tempList.length - 1])
          this.tempList[winId].length--
          this.pageDraw()
        }
        break
      case 3:
        this.pagesData[winId] = []
        this.tempList[winId] = []
        this.pageDraw()
        break
      case 6:
        this.tempList[winId] = []
        break
      case 7:
        this.pagesData[winId] = []
        this.pageDraw()
        break
    }
  }

  /**
   * 销毁
   */
  public destroy() {
    this.dom && (this.dom.innerHTML = '')
    this.pagesData = {}
    this.tempList = {}
    this.removeAllEventListener()
  }

  /**
   * 销毁
   */
  public removeAllEventListener() {
    window.removeEventListener('mousedown', this._drawDown.bind(this))
    window.removeEventListener('mousemove', this._drawMove.bind(this))
    window.removeEventListener('mouseup', this._drawUp.bind(this))
    window.removeEventListener('resize', this._resize.bind(this))
  }
}
